import { createSelector } from '@reduxjs/toolkit'

import { queryView, type QueryResultLike, type QueryView } from '@krp/client-runtime'

import type { ProbeResult } from '../api/contracts'
import { ALLOWED, denied, grantsOf, registryManageAction, resourceCapabilityAction } from '../../access/model/grants'
import type { MyAccess } from '../../access/model/types'
import { EMPTY_CONNECTION_FORMS, type ConnectionFormRegistry } from './connectionForms'
import { EMPTY_CONNECTORS, createConnectorRegistry, type ConnectorManifest, type ConnectorRegistry } from './connectors'
import { mutationView, type MutationResultLike } from './mutation'
import { EMPTY_RESOURCE_ENTRIES, type ResourceEntryRegistry } from './resourceEntries'
import { MODULE_ANSWERING, MODULE_NEVER_CHECKED, MODULE_NOT_ANSWERING } from './healthTexts'
import { sourceLevel } from './sourceHealth'
import type {
  AccessGrants,
  ActionVm,
  AddKindVm,
  CapabilityTextVm,
  ModuleErrorVm,
  ProbeResultVm,
  ResourceCapabilityVm,
  ResourceCardVm,
  ResourceDetails,
  ResourceDraft,
  ResourceEditorVm,
  ResourceEntryVm,
  ResourceModuleVm,
  ResourceRowVm,
  ResourcesVm,
  ResourceSummary,
  SourceAddVm,
} from './types'

/**
 * Именованные селекторы фичи (D-061, D-062). Вход — НЕ RootState, а то, что
 * binding приложения выбрал: результат endpoint, мои права и реестр форм. Пакет компилируется и тестируется без приложения (F03).
 */
export interface ResourcesVmInput {
  query: QueryResultLike<readonly ResourceSummary[]>
  /** Мои права; не передано — права неизвестны, команды недоступны с причиной. */
  access?: QueryResultLike<MyAccess>
  forms?: ConnectionFormRegistry
}

/** Вход окна «Добавить источник»: мои права, виды с сервера (манифесты) и формы пакетов видов — слова и поля настроек. */
export interface SourceAddVmInput {
  access?: QueryResultLike<MyAccess>
  connectors?: QueryResultLike<readonly ConnectorManifest[]>
  forms?: ConnectionFormRegistry
}

export interface ResourceCardVmInput {
  query: QueryResultLike<ResourceSummary>
  access?: QueryResultLike<MyAccess>
  forms?: ConnectionFormRegistry
  /** Манифесты видов: из них — модули источника; не переданы или вида нет — карточка показывает права списком. */
  connectors?: ConnectorRegistry
  probe?: MutationResultLike<ProbeResult>
  /** Входы в окна пакетов по виду; не передано — карточка без входов. */
  entries?: ResourceEntryRegistry
}

export interface ResourceEditorVmInput {
  mode: 'create' | 'edit'
  /** Вид: для создания — из адреса окна; для правки — из загруженного ресурса. */
  kind: string
  query?: QueryResultLike<ResourceDetails>
  access?: QueryResultLike<MyAccess>
  forms: ConnectionFormRegistry
  /** Виды с сервера: права нового ресурса и догонка прав существующего берутся из манифеста вида, а не из клиента. */
  connectors?: QueryResultLike<readonly ConnectorManifest[]>
  save?: MutationResultLike<ResourceSummary>
}

/** Право, по которому окно диагностики native-службы доступно (оба манифеста). */
export const DIAGNOSTICS_CAPABILITY = 'diagnostics.read'

const EMPTY_RESOURCES: readonly ResourceSummary[] = []
const NO_MODULES: readonly ResourceModuleVm[] = []
const NO_CONNECTORS_QUERY: QueryResultLike<readonly ConnectorManifest[]> = {
  isUninitialized: true,
  isLoading: false,
  isError: false,
  isSuccess: false,
}
const NO_CAPABILITIES: readonly ResourceCapabilityVm[] = []

function capabilityText(forms: ConnectionFormRegistry, kind: string, capability: string): CapabilityTextVm {
  return { label: forms.capabilityLabelOf(kind, capability), hint: forms.capabilityHintOf(kind, capability) }
}
const NO_ENTRIES: readonly ResourceEntryVm[] = []
const NO_KINDS: readonly AddKindVm[] = []
const NO_ACCESS: QueryResultLike<MyAccess> = { isUninitialized: true, isLoading: false, isError: false, isSuccess: false }

/** Вид запроса из полей результата — общий для окон фичи. */
function selectQueryView<I, T>(pick: (input: I) => QueryResultLike<T>, isEmpty: (data: T) => boolean) {
  return createSelector(
    [
      (input: I) => pick(input).isUninitialized,
      (input: I) => pick(input).isLoading,
      (input: I) => pick(input).isError,
      (input: I) => pick(input).isSuccess,
      (input: I) => pick(input).error,
      (input: I) => pick(input).data,
    ],
    (isUninitialized, isLoading, isError, isSuccess, error, data): QueryView =>
      queryView(
        {
          isUninitialized,
          isLoading,
          isError,
          isSuccess,
          ...(error !== undefined ? { error } : {}),
          ...(data !== undefined ? { data } : {}),
        },
        isEmpty,
      ),
  )
}

function grantsFrom(data: MyAccess | undefined, isError: boolean): AccessGrants {
  return grantsOf({ isUninitialized: false, isLoading: false, isSuccess: !isError, isError, ...(data ? { data } : {}) })
}

function toRow(resource: ResourceSummary, forms: ConnectionFormRegistry): ResourceRowVm {
  return {
    id: resource.id,
    name: resource.displayName,
    kind: resource.kind,
    mark: forms.shortTitleOf(resource.kind),
    kindTitle: forms.sourceNounOf(resource.kind),
    tone: forms.toneOf(resource.kind),
    enabled: resource.enabled,
  }
}

/**
 * Можно ли добавлять и что: права реестра и виды, которые объявил сервер (манифесты). Форма пакета вида даёт плитке слова; вид
 * без формы показывается с причиной, а не пропадает. Одно правило для кнопки и для плиток.
 */
function addOptions(grants: AccessGrants, forms: ConnectionFormRegistry, connectors: ConnectorRegistry, kindsQuery: QueryView): SourceAddVm {
  const manage = registryManageAction(grants)
  if (!manage.allowed) return { add: manage, kindsQuery, kinds: NO_KINDS }
  return {
    add: ALLOWED,
    kindsQuery,
    // Виды — в общем порядке источников (решение владельца 03.11.2026), а не в порядке ответа сервера.
    kinds: [...connectors.all].sort((left, right) => forms.compareKinds(left.kind, right.kind)).map((manifest) => ({
      kind: manifest.kind,
      title: forms.titleOf(manifest.kind),
      mark: forms.shortTitleOf(manifest.kind),
      summary: forms.summaryOf(manifest.kind),
      pick: forms.find(manifest.kind) === undefined ? denied(`Форма подключения вида «${manifest.kind}» не зарегистрирована`) : ALLOWED,
    })),
  }
}

/** Реестр манифестов из ответа сервера; один экземпляр на селектор, чтобы мемоизация не ломалась. */
const makeSelectConnectorRegistry = <I>(pick: (input: I) => QueryResultLike<readonly ConnectorManifest[]> | undefined) =>
  createSelector([(input: I) => pick(input)?.data], (data) => (data === undefined ? EMPTY_CONNECTORS : createConnectorRegistry(data)))

/**
 * Фабрика: один экземпляр селектора на смонтированный binding (D07), иначе
 * мемоизация ломается, когда два окна списка открыты одновременно.
 */
export const makeSelectResourcesVm = () => {
  const selectData = (input: ResourcesVmInput) => input.query.data ?? EMPTY_RESOURCES
  const selectForms = (input: ResourcesVmInput) => input.forms ?? EMPTY_CONNECTION_FORMS
  const selectGrants = createSelector(
    [
      (input: ResourcesVmInput) => (input.access ?? NO_ACCESS).data,
      (input: ResourcesVmInput) => (input.access ?? NO_ACCESS).isError,
    ],
    grantsFrom,
  )

  // Порядок источников — общий для всех списков (решение владельца 03.11.2026): по виду, затем по названию.
  const selectRows = createSelector([selectData, selectForms], (data, forms) => forms.sortSources(data).map((resource) => toRow(resource, forms)))
  const selectAdd = createSelector([selectGrants], registryManageAction)
  const selectView = selectQueryView<ResourcesVmInput, readonly ResourceSummary[]>(
    (input) => input.query,
    (items) => items.length === 0,
  )

  return createSelector([selectView, selectRows, selectAdd], (query, rows, add): ResourcesVm => ({ query, rows, add }))
}

/** Окно «Добавить источник»: доступность и плитки видов с сервера. Один экземпляр на смонтированный binding (D07). */
export const makeSelectSourceAddVm = () => {
  const selectForms = (input: SourceAddVmInput) => input.forms ?? EMPTY_CONNECTION_FORMS
  const selectGrants = createSelector(
    [(input: SourceAddVmInput) => (input.access ?? NO_ACCESS).data, (input: SourceAddVmInput) => (input.access ?? NO_ACCESS).isError],
    grantsFrom,
  )
  const selectRegistry = makeSelectConnectorRegistry<SourceAddVmInput>((input) => input.connectors)
  const selectKindsView = selectQueryView<SourceAddVmInput, readonly ConnectorManifest[]>(
    (input) => input.connectors ?? NO_CONNECTORS_QUERY,
    (items) => items.length === 0,
  )
  return createSelector([selectGrants, selectForms, selectRegistry, selectKindsView], addOptions)
}

/**
 * Ресурсы, где право включено И выдано человеку, — для окон, которым нужен
 * подходящий экземпляр (источник/получатель, диагностика). Общего «выбранного
 * ресурса» нет: окно получает rid явно.
 */
export interface ResourcesWithCapabilityInput {
  resources: readonly ResourceSummary[] | undefined
  access: QueryResultLike<MyAccess>
  capability: string
}

export const makeSelectResourcesWithCapability = () =>
  createSelector(
    [
      (input: ResourcesWithCapabilityInput) => input.resources ?? EMPTY_RESOURCES,
      (input: ResourcesWithCapabilityInput) => input.access.data,
      (input: ResourcesWithCapabilityInput) => input.access.isError,
      (input: ResourcesWithCapabilityInput) => input.capability,
    ],
    (resources, data, isError, capability): readonly ResourceSummary[] => {
      const grants = grantsFrom(data, isError)
      return resources.filter((resource) => resourceCapabilityAction(grants, resource, capability).allowed)
    },
  )

/** Итог проверки связи словами карточки: связь есть, пока нет кода ошибки и статус не «недоступно». */
const isAnswering = (status: string, errorCode: string | null): boolean => errorCode === null && status.toLowerCase() !== 'unavailable'

function probeResultOf(data: ProbeResult): ProbeResultVm {
  return {
    ok: isAnswering(data.status, data.errorCode),
    status: data.status,
    code: data.errorCode,
    ...(data.modules !== undefined
      ? { modules: data.modules.map((part) => ({ id: part.id, ok: isAnswering(part.status, part.errorCode), status: part.status, code: part.errorCode })) }
      : {}),
  }
}

type ModuleState = Pick<ResourceModuleVm, 'level' | 'stateText' | 'error'>
type ProbeView = { state: { busy: boolean }; result: ProbeResultVm | null }

const answering = (): ModuleState => ({ level: 'ok', stateText: MODULE_ANSWERING, error: null })
const notAnswering = (error: ModuleErrorVm): ModuleState => ({ level: 'down', stateText: MODULE_NOT_ANSWERING, error })

/**
 * Как живёт модуль — только по тому, что сообщил сервер, без выдумывания: итог проверки связи по этому модулю; у вида из одного
 * модуля — общий итог проверки или связь ресурса (SRC-HEALTH). Иначе «сервер не сообщает»: молчание не выдаётся за «жив».
 */
function moduleStateOf(resource: ResourceSummary, moduleId: string, single: boolean, probe: ProbeView): ModuleState {
  if (probe.state.busy) return { level: 'checking', stateText: 'Проверяется…', error: null }
  const result = probe.result
  const own = result?.modules?.find((part) => part.id === moduleId)
  if (own !== undefined) return own.ok ? answering() : notAnswering({ message: own.status, code: own.code })
  if (single && result !== null) return result.ok ? answering() : notAnswering({ message: result.status, code: result.code })
  if (single) {
    const contact = resource.contact ?? null
    const level = sourceLevel(contact)
    if (contact !== null && level === 'ok') return answering()
    if (contact !== null && level === 'down') {
      const error = contact.state === 'error' ? contact.lastError : null
      return notAnswering({
        message: error?.message ?? contact.reason ?? 'Источник не отвечает',
        code: error === null ? contact.reasonCode : error.code !== null ? String(error.code) : error.kind,
      })
    }
    return { level: 'unknown', stateText: MODULE_NEVER_CHECKED, error: null }
  }
  return {
    level: 'unknown',
    stateText: result === null ? 'Состояние модуля сервер не сообщает' : 'Итог проверки по этому модулю сервер не сообщил',
    error: null,
  }
}

/** Модули источника по манифесту вида: название, права у ресурса и состояние. Манифеста нет — модулей нет. */
function modulesOf(
  resource: ResourceSummary,
  manifest: ConnectorManifest | undefined,
  forms: ConnectionFormRegistry,
  probe: ProbeView,
  retry: ActionVm,
): readonly ResourceModuleVm[] {
  if (manifest === undefined) return NO_MODULES
  const enabledOf = new Map(resource.capabilities.map((capability): [string, boolean] => [capability.name, capability.enabled]))
  return manifest.modules.map((part) => ({
    id: part.id,
    title: forms.moduleTitleOf(resource.kind, part.id),
    hint: forms.moduleHintOf(resource.kind, part.id),
    // Право из манифеста, которого у ресурса ещё нет (вид добавил позже), — выключенное: догонка включит его выключенным.
    capabilities: part.capabilities.map((name) => ({
      name,
      enabled: enabledOf.get(name) ?? false,
      ...capabilityText(forms, resource.kind, name),
    })),
    ...moduleStateOf(resource, part.id, manifest.modules.length === 1, probe),
    retry,
  }))
}

export const makeSelectResourceCardVm = () => {
  const selectView = selectQueryView<ResourceCardVmInput, ResourceSummary>((input) => input.query, () => false)
  const selectGrants = createSelector(
    [
      (input: ResourceCardVmInput) => (input.access ?? NO_ACCESS).data,
      (input: ResourceCardVmInput) => (input.access ?? NO_ACCESS).isError,
    ],
    grantsFrom,
  )
  const selectProbe = createSelector(
    [
      (input: ResourceCardVmInput) => input.probe?.isLoading ?? false,
      (input: ResourceCardVmInput) => input.probe?.isError ?? false,
      (input: ResourceCardVmInput) => input.probe?.isSuccess ?? false,
      (input: ResourceCardVmInput) => input.probe?.error,
      (input: ResourceCardVmInput) => input.probe?.data,
    ],
    (isLoading, isError, isSuccess, error, data) => ({
      state: mutationView({ isLoading, isError, isSuccess, ...(error !== undefined ? { error } : {}) }),
      result: isSuccess && data !== undefined ? probeResultOf(data) : null,
    }),
  )

  return createSelector(
    [
      selectView,
      (input: ResourceCardVmInput) => input.query.data,
      selectGrants,
      (input: ResourceCardVmInput) => input.forms ?? EMPTY_CONNECTION_FORMS,
      selectProbe,
      (input: ResourceCardVmInput) => input.entries ?? EMPTY_RESOURCE_ENTRIES,
      (input: ResourceCardVmInput) => input.connectors ?? EMPTY_CONNECTORS,
    ],
    (query, data, grants, forms, probe, registry, connectors): ResourceCardVm => {
      // Ошибка при старых данных — ошибка, а не старые данные как актуальные (F06).
      const resource = query.status === 'ready' ? (data ?? null) : null
      const manage = registryManageAction(grants)
      return {
        query,
        resource,
        kindTitle: resource === null ? '' : forms.titleOf(resource.kind),
        capabilities:
          resource === null
            ? NO_CAPABILITIES
            : resource.capabilities.map((capability) => ({
                name: capability.name,
                enabled: capability.enabled,
                ...capabilityText(forms, resource.kind, capability.name),
              })),
        edit:
          resource !== null && manage.allowed && forms.find(resource.kind) === undefined
            ? denied(`Форма подключения вида «${resource.kind}» не зарегистрирована`)
            : manage,
        modules: resource === null ? NO_MODULES : modulesOf(resource, connectors.find(resource.kind), forms, probe, manage),
        probe: manage,
        probeState: probe.state,
        probeResult: probe.result,
        diagnostics:
          resource === null ? denied('Ресурс не загружен') : resourceCapabilityAction(grants, resource, DIAGNOSTICS_CAPABILITY),
        entries:
          resource === null
            ? NO_ENTRIES
            : registry.of(resource.kind).map((entry) => ({
                window: entry.window,
                label: entry.label,
                action: resourceCapabilityAction(grants, resource, entry.capability),
              })),
      }
    },
  )
}

const READY: QueryView = { status: 'ready', refreshing: false, error: null }

/** Черновик формы из ресурса: права — как есть, настройки — копия. */
export function draftOf(resource: ResourceDetails): ResourceDraft {
  return {
    displayName: resource.displayName,
    enabled: resource.enabled,
    capabilities: resource.capabilities.map((capability) => ({ ...capability })),
    settings: { ...resource.settings },
  }
}

export const makeSelectResourceEditorVm = () => {
  const selectView = selectQueryView<ResourceEditorVmInput, ResourceDetails>(
    (input) => input.query ?? { isUninitialized: true, isLoading: false, isError: false, isSuccess: false },
    () => false,
  )
  const selectGrants = createSelector(
    [
      (input: ResourceEditorVmInput) => (input.access ?? NO_ACCESS).data,
      (input: ResourceEditorVmInput) => (input.access ?? NO_ACCESS).isError,
    ],
    grantsFrom,
  )
  const selectSave = createSelector(
    [
      (input: ResourceEditorVmInput) => input.save?.isLoading ?? false,
      (input: ResourceEditorVmInput) => input.save?.isError ?? false,
      (input: ResourceEditorVmInput) => input.save?.error,
    ],
    (isLoading, isError, error) =>
      mutationView({ isLoading, isError, isSuccess: false, ...(error !== undefined ? { error } : {}) }),
  )
  const selectCreated = createSelector(
    [
      (input: ResourceEditorVmInput) => input.mode,
      (input: ResourceEditorVmInput) => (input.save?.isSuccess === true ? input.save.data : undefined),
    ],
    (mode, data) => (mode === 'create' && data !== undefined ? { id: data.id, name: data.displayName } : null),
  )
  const selectRegistry = makeSelectConnectorRegistry<ResourceEditorVmInput>((input) => input.connectors)
  const selectKindsView = selectQueryView<ResourceEditorVmInput, readonly ConnectorManifest[]>(
    (input) => input.connectors ?? NO_CONNECTORS_QUERY,
    () => false,
  )
  const selectInitial = createSelector(
    [
      (input: ResourceEditorVmInput) => input.mode,
      (input: ResourceEditorVmInput) => input.kind,
      (input: ResourceEditorVmInput) => input.forms,
      selectRegistry,
      selectKindsView,
      (input: ResourceEditorVmInput) => input.query?.data,
      (input: ResourceEditorVmInput) => input.query?.isError ?? false,
    ],
    (mode, kind, forms, connectors, kindsView, data, isError): ResourceDraft | null => {
      const form = forms.find(kind)
      const manifest = connectors.find(kind)
      // Права вида — из манифеста с сервера: пока он не пришёл (или вида в нём нет), форме нечего предложить.
      if (kindsView.status !== 'ready' || manifest === undefined) return null
      if (mode === 'edit') {
        if (data === undefined || isError) return null
        const draft = draftOf(data)
        // Права манифеста, которых у ресурса ещё нет (добавлены в вид позже), — выключенными, чтобы их можно было включить.
        const known = new Set(draft.capabilities.map((capability) => capability.name))
        const added = manifest.capabilities.filter((name) => !known.has(name)).map((name) => ({ name, enabled: false }))
        return added.length === 0 ? draft : { ...draft, capabilities: [...draft.capabilities, ...added] }
      }
      if (form === undefined) return null
      return {
        displayName: '',
        enabled: true,
        capabilities: manifest.capabilities.map((name) => ({ name, enabled: true })),
        settings: form.emptySettings(),
      }
    },
  )

  return createSelector(
    [
      (input: ResourceEditorVmInput) => input.mode,
      (input: ResourceEditorVmInput) => input.kind,
      (input: ResourceEditorVmInput) => input.forms,
      selectView,
      selectInitial,
      selectGrants,
      selectSave,
      selectCreated,
      selectRegistry,
      selectKindsView,
    ],
    (mode, kind, forms, view, initial, grants, saveState, created, connectors, kindsView): ResourceEditorVm => {
      const form = forms.find(kind)
      const manage = registryManageAction(grants)
      const manifestMissing = kindsView.status === 'ready' && connectors.find(kind) === undefined
      const formAction =
        form === undefined
          ? denied(`Форма подключения вида «${kind}» не зарегистрирована`)
          : manifestMissing
            ? denied(`Сервер не объявил вид «${kind}»: права и модули неизвестны`)
            : ALLOWED
      // Ресурс (для правки) и виды с сервера (манифест вида) нужны оба: пока один не готов, форма показывает его состояние.
      const resourceView = mode === 'create' ? READY : view
      return {
        mode,
        kind,
        kindTitle: forms.titleOf(kind),
        query: resourceView.status !== 'ready' ? resourceView : kindsView,
        initial,
        form: formAction,
        save: !manage.allowed ? manage : formAction,
        saveState,
        secret: form?.secret ?? null,
        created,
        stale: saveState.error?.code === 'resource-changed-concurrently',
        capabilityTexts: Object.fromEntries(
          (initial?.capabilities ?? []).map((capability) => [capability.name, capabilityText(forms, kind, capability.name)]),
        ),
      }
    },
  )
}
